/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ActionMenu,
  ActionMenuProps,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_PRIMARY,
  DIALOG_SIZE_SM,
  DROPDOWN_POSITION_RIGHT,
  DialogContentProps,
  DialogProps,
  DropdownItemData,
  EmptyState,
  EmptyStateProps,
  ICON_SWITCH_ACCESS_SHORTCUT_WITH_LIST,
  SPINNER_TYPE_REGULAR,
  Spinner,
  Table,
  TableCell,
} from '@squiz/sds';
import { EntityDxpUserMeOrganisation } from 'apps/console-core/src/Services/console';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useConsoleServiceStore, useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { useInsights } from '../../../../Services/insights';
import {
  EVENT_RULE_TYPE_CUSTOM,
  EVENT_RULE_TYPE_LOGIN,
  EVENT_RULE_TYPE_REQUEST,
  EVENT_RULE_TYPE_VISIT,
  EntitySegmentEventSubscription,
  useSegmentEvents,
} from '../../../../Services/segmentation';
import { EVENTS_DATA_IDS } from '../../Events.constants';
import {
  EVENTS_LIST_SOURCE_DATA_INTEGRATION,
  EVENTS_LIST_SOURCE_WEB_BEHAVIOR,
  EVENTS_LIST_TYPE_CUSTOM,
  EVENTS_LIST_TYPE_LOGIN,
  EVENTS_LIST_TYPE_PAGE_VIEW,
  EVENTS_LIST_TYPE_VISIT,
  MAX_CHARACTERS,
} from './EventsList.constants';
import { EventsListItem } from './EventsList.types';
import { DeleteEventDialog } from './elements/DeleteEventDialog';
import { DeleteEventDialogProps } from './elements/DeleteEventDialog.props';

/**
 * Convert an event type to a translation string.
 * @param type The event type.
 * @returns The translation string.
 */
export function convertEventTypeToLabel(type: EntitySegmentEventSubscription['type']): string {
  switch (type) {
    case EVENT_RULE_TYPE_LOGIN:
      return EVENTS_LIST_TYPE_LOGIN;
    case EVENT_RULE_TYPE_VISIT:
      return EVENTS_LIST_TYPE_VISIT;
    case EVENT_RULE_TYPE_CUSTOM:
      return EVENTS_LIST_TYPE_CUSTOM;
    case EVENT_RULE_TYPE_REQUEST:
    default:
      return EVENTS_LIST_TYPE_PAGE_VIEW;
  }
}

export const EventsList = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cacheKey, setCacheKey] = useState<string>(``);
  const { tenant }: { tenant: EntityDxpUserMeOrganisation | null } = useConsoleServiceStore();
  const insightsList = useInsights(cacheKey);
  const eventsList = useSegmentEvents(cacheKey);
  const { updateActiveDialog } = useDialogStore();

  // Build an event list to display from the raw event and insight data.
  const eventDisplayList = useMemo(() => {
    const events: Array<EventsListItem> = [];
    if (!eventsList || !insightsList) {
      return events;
    }
    Object.entries(insightsList).forEach(([insightId, insight]) => {
      events.push({
        id: insightId,
        name: insight.name,
        source: EVENTS_LIST_SOURCE_DATA_INTEGRATION,
        type: null,
      });
    });
    Object.entries(eventsList).forEach(([eventId, event]) => {
      events.push({
        id: eventId,
        name: event.name,
        source: EVENTS_LIST_SOURCE_WEB_BEHAVIOR,
        type: t(convertEventTypeToLabel(event.type)),
      });
    });
    return events.sort((a, b): number => {
      if (a.name > b.name) {
        return 1;
      }
      return -1;
    });
  }, [eventsList, insightsList, t]);

  // Labels
  const labels = useMemo(() => {
    return {
      actionMenuAriaLabel: t(CDP_STRINGS.eventsActionMenuAriaLabel),
      createEventFeed: t(CDP_STRINGS.createEventFeed),
      deleteEvent: t(CDP_STRINGS.eventsActionMenuDelete),
      deleteEventDialogHeading: t(CDP_STRINGS.deleteEventDialogHeading),
      eventFeedDataIntegrationLabel: t(CDP_STRINGS.eventFeedDataIntegrationLabel),
      eventFeedWebBehaviorLabel: t(CDP_STRINGS.eventFeedWebBehaviorLabel),
      noEventsCaption: t(CDP_STRINGS.noEventsCaption),
      tableHeaders: {
        eventName: t(CDP_STRINGS.tableHeaderEventName),
        source: t(CDP_STRINGS.tableHeaderEventSource),
        type: t(CDP_STRINGS.tableHeaderEventType),
      },
      viewEvent: t(CDP_STRINGS.eventsActionMenuView),
    };
  }, [t]);

  const hasCreatePermission = hasPermission(PERMISSIONS.dxpEvents.EVENT_CREATE);

  // Truncate lengthy text and add ellipsis
  const truncateText = (text: string, maxChars = MAX_CHARACTERS): string => {
    const textTrim = text.trim();
    return text.length > maxChars ? text.substring(0, maxChars) + '...' : textTrim;
  };

  if (!tenant || !insightsList || !eventsList) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const getEventList = (): JSX.Element => {
    const hasDeletePermission = hasPermission(PERMISSIONS.dxpEvents.EVENT_DELETE);
    const tableColumns: Array<TableCell> = [
      {
        content: labels.tableHeaders.eventName,
        key: `eventName`,
      },
      {
        content: labels.tableHeaders.source,
        key: `source`,
      },
      {
        content: labels.tableHeaders.type,
        key: `type`,
      },
      {
        content: ``,
        key: `actions`,
      },
    ];
    const tableRows: Array<object> = eventDisplayList.map((event) => {
      const { id, name, source, type } = event;
      const titleToDisplay = truncateText(name || ``);

      const dialogProps: DialogProps = {
        dialogContent: {
          renderContent: (props: DialogContentProps): JSX.Element => {
            const dialogProps: DeleteEventDialogProps = {
              ...props,
              clearCache: setCacheKey,
              id,
              name,
              source,
            };

            return <DeleteEventDialog {...dialogProps} />;
          },
        },
        dialogSize: DIALOG_SIZE_SM,
        heading: labels.deleteEventDialogHeading,
        preventFullScreen: true,
        scrollOnOverflow: false,
        stateHandler: useDialogStore,
      };

      const menuItems: Array<DropdownItemData> = [
        {
          action: (): void =>
            navigate({
              pathname: getCapabilityPath(`/event/${id}`),
            }),
          dataID: EVENTS_DATA_IDS.eventActionMenuItemViewDetails,
          key: `0`,
          label: labels.viewEvent,
        },
      ];

      if (hasDeletePermission) {
        // Delete event menu item
        menuItems.push({
          action: (): void => updateActiveDialog(dialogProps),
          dataID: EVENTS_DATA_IDS.eventActionMenuItemDelete,
          key: `1`,
          label: labels.deleteEvent,
        });
      }

      const actionMenuProps: ActionMenuProps = {
        buttonLabel: labels.actionMenuAriaLabel,
        classes: `event-item__menu`,
        dropdownPosition: DROPDOWN_POSITION_RIGHT,
        itemList: menuItems,
        sectionKey: EVENTS_DATA_IDS.eventActionMenuButton,
      };

      const navigateToEvent = (): void =>
        navigate({
          pathname: getCapabilityPath(`/event/${id}`),
        });

      return {
        actions: <ActionMenu {...actionMenuProps} data-id={EVENTS_DATA_IDS.eventActionMenuButton} />,
        eventName: (
          <button
            className="event-item__link"
            onClick={(): void => {
              navigateToEvent();
            }}
            onKeyDown={(event: React.KeyboardEvent): void => {
              const actionMenuSelector = `[data-id="${EVENTS_DATA_IDS.eventActionMenuButton}"]`;
              const actionMenuButton = (event.target as HTMLDivElement).closest(actionMenuSelector);
              const navigateKeys = ['Enter', 'Space'];
              const shouldNavigate = !actionMenuButton && navigateKeys.includes(event.code);
              if (shouldNavigate) {
                navigateToEvent();
              }
            }}
          >
            {titleToDisplay}
          </button>
        ),
        source,
        type: type === null ? <div>-</div> : type,
      };
    });
    return <Table classes={`events-list__table`} tableContent={{ columns: tableColumns, rows: tableRows }} />;
  };

  const createButtonMenuItems: Array<DropdownItemData> = [
    {
      action: (): void =>
        navigate({
          pathname: getCapabilityPath(`/event/create-web-behavior`),
        }),
      dataID: EVENTS_DATA_IDS.eventActionMenuCreateWebBehavior,
      key: `1`,
      label: labels.eventFeedWebBehaviorLabel,
    },
    {
      action: (): void =>
        navigate({
          pathname: getCapabilityPath(`/event/create-data-integration`),
        }),
      dataID: EVENTS_DATA_IDS.eventActionMenuCreateDataIntegration,
      key: `0`,
      label: labels.eventFeedDataIntegrationLabel,
    },
  ];

  const createButtonMenuProps: ActionMenuProps = {
    // @ts-ignore.
    buttonProps: {
      classes: [`event-create__button`],
      format: BUTTON_FORMAT_STRONG,
      label: labels.createEventFeed,
      text: labels.createEventFeed,
      theme: BUTTON_THEME_PRIMARY,
    },
    classes: `event-create__menu`,
    disabled: !hasCreatePermission,
    dropdownPosition: DROPDOWN_POSITION_RIGHT,
    itemList: createButtonMenuItems,
    sectionKey: EVENTS_DATA_IDS.createEvent,
  };

  const createButton = <ActionMenu {...createButtonMenuProps} data-id={EVENTS_DATA_IDS.createEvent} />;

  const hasEvents = Object.keys(eventDisplayList).length > 0;
  if (hasEvents) {
    return (
      <div className="events-list">
        <div className="events-list__actions">{createButton}</div>
        {getEventList()}
      </div>
    );
  }

  const emptyStateClass = hasCreatePermission ? `sds-empty-state-card--with-cta` : ``;
  const emptyStateProps: EmptyStateProps = {
    caption: labels.noEventsCaption,
    classes: emptyStateClass,
    dataId: EVENTS_DATA_IDS.eventsListEmptyState,
  };

  return (
    <EmptyState {...emptyStateProps} svg={ICON_SWITCH_ACCESS_SHORTCUT_WITH_LIST}>
      {hasCreatePermission && createButton}
    </EmptyState>
  );
};
